<!-- NotesApp Source File: header.component.html  -->
<app-config class="hidden" />

<div class="bg-surface-100 dark:bg-surface-950 p-0.5 flex flex-col gap-6">
  <div class="flex-row">
    <!-- Mobile App-Head pane. Three groups: menu + title on the left, the Filter / Saved Searches /
         Sort icons in the middle, the User menu on the right. The left and right groups are both
         flex-1 (equal width), so the middle group sits at the true horizontal centre of the page.
         ~50% taller than before (the h-9 icon buttons plus their own touch-target padding now drive
         the row's height, vs. the old near-content-height row) with larger icons throughout, and a
         neutral grey background (bg-surface-100) instead of white, so it reads as app chrome rather
         than another content card. -->
    <div
      class="sm:hidden flex sticky top-0 z-50 flex-grow space-x-1 justify-between items-center py-0.5 px-1 bg-surface-100 dark:bg-surface-900 rounded-2xl mx-auto border border-surface-200 dark:border-surface-700 w-full">
      <div class="flex items-center space-x-1 flex-1 min-w-0">
        <p-tieredMenu
          class="ml-0 mt-6 text-l font-light text-surface-700 dark:text-surface-100 leading-none"
          #mobmenu
          [model]="pageMenuItems"
          [popup]="true"
          appendTo="body">
        </p-tieredMenu>

        <!-- Trigger button -->
        <a
          class="relative flex h-9 w-9 shrink-0 items-center justify-center cursor-pointer text-surface-700 dark:text-surface-100 leading-none"
          aria-label="Menu"
          (click)="togglePageMenu($event, mobmenu)">
          <i class="pi pi-bars text-2xl"></i>
        </a>
        <div class="text-lg font-medium shrink-0" data-testid="header-view-title-mobile">{{ viewTitle }}</div>
        <!-- The mobile search field itself is now a floating bar near the bottom of the screen, owned by
             HomeComponent (home.component.html) so it can sit outside the zoom-scaled layer and stay
             pinned to the true viewport edge. This popup stays here — appendTo="body" means its overlay
             escapes the zoom layer regardless — and is opened from there via toggleMobileSearchTypeMenu(). -->
        <p-menu
          #mobileSearchTypeMenu
          [model]="mobileSearchTypeMenuItems"
          [popup]="true"
          appendTo="body"
          [autoZIndex]="true"
          [baseZIndex]="1000"
          class="hidden"></p-menu>
      </div>

      <div class="flex items-center space-x-1 shrink-0">
        <div class="FilterMenu">
          <p-menu
            #filtermenu
            [model]="filterMenuItems"
            [popup]="true"
            appendTo="body"
            [autoZIndex]="true"
            [baseZIndex]="1000" />

          <a
            class="relative flex h-9 w-9 shrink-0 items-center justify-center cursor-pointer text-primary leading-none"
            title="Open filter menu"
            aria-label="Open filter menu"
            (click)="filtermenu.toggle($event)">
            <i class="pi pi-filter text-2xl"></i>
          </a>
        </div>

        <!-- Saved Searches — a compact icon trigger (not a full <select>, unlike desktop's
             header-saved-search-shell) reusing the exact same menu items the hamburger's "Saved
             Searches/Filters" submenu builds, so there's one source of truth for the list. A small
             dot badge shows when a saved search is currently applied. -->
        <p-menu
          #mobileSavedSearchMenu
          [model]="mobileSavedSearchMenuItems"
          [popup]="true"
          appendTo="body"
          [autoZIndex]="true"
          [baseZIndex]="1000"></p-menu>
        <a
          class="relative flex h-9 w-9 shrink-0 items-center justify-center cursor-pointer text-primary leading-none"
          title="Presets"
          aria-label="Presets"
          (click)="toggleSavedSearchMenu($event, mobileSavedSearchMenu)">
          <i class="pi pi-bookmark text-2xl"></i>
          @if (activeSavedSearchId) {
            <span class="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" aria-hidden="true"></span>
          }
        </a>

        <!-- Sort — mobile only. Options are the same sortBy keys ListComponent.sortData() already
             knows how to toggle the order of; this button just picks the field. -->
        <p-menu
          #mobileSortMenu
          [model]="mobileSortMenuItems"
          [popup]="true"
          appendTo="body"
          [autoZIndex]="true"
          [baseZIndex]="1000"
          styleClass="min-w-60">
          <!-- iOS Files-style: a check before the active field, its direction in small grey text below,
               and an up/down chevron on the right (tapping it again reverses the order). -->
          <ng-template #item let-item>
            <a class="flex min-h-11 items-center gap-2 py-1.5 pl-2 pr-3">
              <i class="pi pi-check w-5 text-sm text-primary" [class.invisible]="!item.state?.selected" aria-hidden="true"></i>
              <span class="flex min-w-0 flex-1 flex-col leading-tight">
                <span [class.font-semibold]="item.state?.selected">{{ item.label }}</span>
                @if (item.state?.selected) {
                  <span class="mt-0.5 text-xs text-[var(--p-text-muted-color)]">{{ item.state.direction }}</span>
                }
              </span>
              @if (item.state?.selected) {
                <i class="pi text-xs text-[var(--p-text-muted-color)]"
                  [ngClass]="item.state.order === 'asc' ? 'pi-chevron-up' : 'pi-chevron-down'"
                  aria-hidden="true"></i>
              }
            </a>
          </ng-template>
        </p-menu>
        <a
          class="relative flex h-9 w-9 shrink-0 items-center justify-center cursor-pointer text-primary leading-none"
          title="Sort notes"
          aria-label="Sort notes"
          (click)="openMobileSortMenu(mobileSortMenu, $event)">
          <span class="material-symbols-outlined text-2xl">sort_by_alpha</span>
        </a>
      </div>

      <div class="flex items-center justify-end flex-1 min-w-0">

      <!-- User menu (mobile) — reuses the same userMenuItems as the desktop user menu further down
           (App Info, User Settings, Toggle Dark Mode, Database switch, Logout). A separate p-menu
           instance rather than sharing desktop's, matching this file's existing convention of a
           dedicated popup per breakpoint (mobileSearchTypeMenu vs the desktop equivalent, filtermenu
           vs the desktop filter menu, etc.) rather than triggering a menu declared inside a
           `hidden sm:block` (display:none on mobile) subtree. -->
      <p-menu
        #mobileUserMenu
        [model]="userMenuItems"
        [popup]="true"
        appendTo="body"
        [autoZIndex]="true"
        [baseZIndex]="1000"></p-menu>
      <a
        class="relative flex h-9 w-9 shrink-0 items-center justify-center cursor-pointer text-primary leading-none mr-1"
        (click)="mobileUserMenu.toggle($event)"
        [attr.aria-label]="'User Menu' + ((auth.username$ | async) ? (' — ' + (auth.username$ | async)) : '')">
        <span class="material-symbols-outlined text-2xl">person</span>
      </a>
      </div>
    </div>

    <!-- Backdrop: while any mobile popup is open, a tap anywhere outside it closes the popup and is
         otherwise swallowed — it must never also trigger whatever was visually underneath (a list
         item, the FAB, etc.). Sitting above all normal page content (including the floating search
         bar/FAB, z-40/z-50) but below the popups themselves (PrimeNG's own baseZIndex="1000"), a tap
         here can only ever hit this element, so the item behind it never receives the click at all.

         Deliberately reads `.visible`, not PrimeNG Menu's own `.overlayVisible`: overlayVisible is
         set true on show() but is never reset back to false anywhere in PrimeNG — once any one of
         these popups had been opened a single time, this condition would be permanently true, and
         the backdrop would sit over the whole screen forever, silently swallowing every future tap
         app-wide. `.visible` is the property Menu/TieredMenu's own show()/hide()/toggle() actually
         flip both ways, on every popup type used here. -->
    @if (mobmenu.visible || filtermenu.visible || mobileUserMenu.visible || mobileSavedSearchMenu.visible || mobileSortMenu.visible || mobileSearchTypeMenu.visible) {
      <div
        class="sm:hidden fixed inset-0 z-[999]"
        (click)="$event.stopPropagation(); mobmenu.hide(); filtermenu.hide(); mobileUserMenu.hide(); mobileSavedSearchMenu.hide(); mobileSortMenu.hide(); mobileSearchTypeMenu.hide()">
      </div>
    }
    <!-- The mobile-only "Saved Search/Filter" select, advanced tag filter button and refresh icon that
         used to live in a second row here have been removed to save vertical space. All three stay
         reachable from the hamburger menu (Saved Searches/Filters, Filter by Tags, Refresh page) —
         nothing is orphaned, just no longer duplicated as a dedicated row. Desktop keeps its own copies
         of these controls further down, untouched. -->

    <!-- Large-Screen App-Head pane -->
    <div
      #desktopHeaderPane
      class="hidden sm:block sticky top-0 z-50 space-x-3 justify-center items-center p-1 bg-surface-0 dark:bg-surface-900 rounded-2xl mx-auto border border-surface-500 dark:border-surface-700 w-full">
      <div class="flex my-2 items-center">
        <!-- <p-menu #menu [model]="pageMenuItems" [popup]="true" appendTo="body" [autoZIndex]="true"
                    [baseZIndex]="1000" [style]="{ marginTop: '8px' }"
                    class="ml-4 text-xl font-light bg-surface-200 text-surface-700 dark:text-surface-100 leading-none !z-1000"></p-menu> -->
        <p-tieredMenu
          class="ml-1 mt-6 text-xl font-light text-surface-700 dark:text-surface-100 leading-none !z-1000"
          #menu
          [model]="pageMenuItems"
          [popup]="true"
          styleClass="custom-menu-pane"
          appendTo="body">
        </p-tieredMenu>

        <a
          #menuBtn
          class="relative cursor-pointer text-primary dark:text-surface-100 leading-none font-bold text-2xl"
          (click)="togglePageMenu($event, menu)"
          aria-label="Menu">
          <i class="pi pi-bars"></i>
        </a>

        <button
          type="button"
          class="ml-2 mr-2 shrink-0 text-left text-2xl font-bold whitespace-nowrap cursor-pointer bg-transparent border-0 p-0"
          style="color: var(--p-primary-color);"
          title="Click to switch database (currently {{ dataTarget.current }})"
          data-testid="header-view-title"
          (click)="dataTarget.requestSwitch()">
          {{ viewTitle }}
        </button>
        <p-button
          size="small"
          styleClass="ml-4 mr-4 p-button-rounded p-button-outlined ai-launch-btn"
          label="AI"
          icon="pi pi-sparkles"
          (onClick)="onSelectPageMenuItem('AI')">
        </p-button>
        <a
          class="mx-2 cursor-pointer text-red-500 dark:text-surface-100 leading-none"
          (click)="onSelectPageMenuItem('Refresh')"
          aria-label="">
          <i class="pi pi-refresh"></i>
        </a>

        <span
          class="cursor-pointer select-none"
          [class]="isOnline ? 'online' : 'offline'"
          (click)="onSelectPageMenuItem('Synchronisation')"
          [title]="connectivityLabel + ' — click for sync status'">
          {{ isOnline ? '🟢' : '🔴' }}
        </span>
        <div class="group relative flex p-1 h-10 w-full items-center">
          <!-- Search input -->
          <input
            type="text"
            pInputText
            [(ngModel)]="filter.SearchString"
            placeholder="Search"
            #DesktopSearchField
            class="flex-grow mx-2 p-1 border text-sm pr-24 min-w-[200px]"
            [ngClass]="{ 'filter-active-border': isSearchStringActive }"
            style="width: 100%; max-width: 100%;"
            (input)="onSearch($any($event.target).value)" />

          <!-- 🔽 Search type select -->
          <select
            [(ngModel)]="filter.SearchType"
            (change)="setSearchType($any($event.target).value)"
            class="absolute right-10 top-1/2 transform -translate-y-1/2 text-sm border rounded-md bg-white text-gray-700 pr-6 pl-2 h-7 appearance-none cursor-pointer"
            [ngClass]="{ 'filter-active-border': isSearchTypeActive }">
            <option value="Semantic">Semantic</option>
            <option value="Full text">Full text</option>
            <option value="Title only">Title only</option>
            <option value="Title & Tags">Title & Tags</option>
            <option value="Tags only">Tags only</option>
            <option value="Title & Keywords">Title & Keywords</option>
            <option value="Title, Keywords & Tags">Title, Keywords & Tags</option>
            <option value="Keywords only">Keywords only</option>
            <option value="ID">ID</option>
            <option value="Task title">Task title</option>
            <option value="Task, Note Title, Note Tags">Task, Note Title, Note Tags</option>
          </select>

          <!-- ❌ Clear button -->
          <button
            class="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 border-0 bg-transparent"
            type="button"
            (click)="clearSearchField()">
            <i class="pi pi-times text-red-500"></i>
          </button>
        </div>

        <!-- Right menu -->
        <div class="flex-grow"></div>
        <p-menu
          #userMenu
          [model]="userMenuItems"
          [popup]="true"
          appendTo="body"
          [autoZIndex]="true"
          [baseZIndex]="1000"></p-menu>
        <a
          #userMenuBtn
          class="relative flex items-center gap-1 cursor-pointer text-primary dark:text-surface-100 leading-none"
          (click)="userMenu.toggle($event)"
          [attr.aria-label]="'User Menu' + ((auth.username$ | async) ? (' — ' + (auth.username$ | async)) : '')">
          <span class="material-symbols-outlined text-base">person_4</span>
          <span
            *ngIf="auth.username$ | async as currentUsername"
            class="hidden md:inline text-sm max-w-[140px] truncate"
            [title]="currentUsername">{{ currentUsername }}</span>
        </a>
      </div>
      <!-- filter row -->
      <div
        #desktopFilterRow
        class="flex mx-1 items-center flex-nowrap overflow-hidden gap-1 filter-row"
        [ngClass]="{
          'filter-row--compact-actions': filterRowMode === 'compact-actions' || filterRowMode === 'compact-priority',
          'filter-row--compact-priority': filterRowMode === 'compact-priority'
        }">
        <p-splitButton
          size="small"
          [severity]="filterActive ? 'danger' : 'secondary'"
          [styleClass]="'p-0 header-filter-button header-filter-button--clear header-filter-clear-split' + (filterActive ? '' : ' header-filter-clear-split--idle')"
          label="Clear"
          icon="pi pi-filter-slash"
          [model]="clearMenuItems"
          [attr.title]="clearFilterTooltip"
          (onClick)="clearSearchAndFilters()">
        </p-splitButton>
        <div class="mx-1 header-space-filter-shell">
          <p-multiselect
            [options]="spaceFilterOptions"
            optionLabel="label"
            optionValue="id"
            appendTo="body"
            [maxSelectedLabels]="100"
            [title]="spaceFilterSummary"
            class="!p-0 my-0 text-sm leading-none h-9 w-44 space-filter-select"
            [ngClass]="{ 'filter-active-border': isSpaceFilterActive }"
            [ngModel]="selectedSpaceIds"
            (ngModelChange)="onSpaceFilterChanged($event)"
            placeholder="Spaces"
            ariaLabel="Space filter">
            <!-- Shared Spaces: red, with the "group" icon — same as the Space column in the List table. -->
            <ng-template #item let-option>
              <ng-container *ngTemplateOutlet="spaceFilterLabel; context: { $implicit: option }"></ng-container>
            </ng-template>
            <ng-template #selecteditems let-options>
              @for (option of options; track option.id; let last = $last) {
                <ng-container *ngTemplateOutlet="spaceFilterLabel; context: { $implicit: option }"></ng-container>@if (!last) {<span>, </span>}
              }
            </ng-template>
          </p-multiselect>
        </div>
        <div class="flex items-center header-tags-filter-shell">
          <button
            type="button"
            pButton
            class="header-filter-button header-filter-button--tags"
            [ngClass]="{ 'filter-active-border': isTagFilterActive }"
            (click)="toggleQuickTagPopover($event, quickTagPopover)"
            [title]="tagFilterTooltipLabel"
            aria-label="Filter by tags">
            <span class="material-symbols-outlined header-tags-filter-btn__icon" aria-hidden="true">label</span>
            <span class="header-tags-filter-btn__label">{{ tagFilterButtonLabel }}</span>
          </button>

          <p-popover
            #quickTagPopover
            [dismissable]="true"
            [focusOnShow]="false"
            appendTo="body"
            styleClass="header-tags-filter-popover"
            (onShow)="onQuickTagPopoverShow()">
            <div class="header-tags-filter-popover__content">
              <div class="header-tags-filter-popover__title">Filter by tags</div>

              <input
                #quickTagSearchField
                type="text"
                pInputText
                [(ngModel)]="quickTagSearch"
                placeholder="Search tags"
                class="w-full text-sm header-tags-filter-popover__search" />

              <div *ngIf="selectedQuickTagOptions.length > 0" class="header-tags-filter-popover__selected">
                <div class="header-tags-filter-popover__selected-title">Selected tags</div>
                <div class="header-tags-filter-popover__selected-list">
                  <button
                    *ngFor="let tag of selectedQuickTagOptions"
                    type="button"
                    class="header-tags-filter-popover__selected-chip"
                    [style.borderColor]="tag.color || '#2563eb'"
                    [style.backgroundColor]="tag.color || '#2563eb'"
                    [title]="tag.name + ' — ' + tagContextLabel(tag)"
                    (click)="toggleQuickTag(tag._id)">
                    @if (isPrivateTagOption(tag)) {
                      <i class="pi pi-lock !text-[0.625rem]" aria-label="Private tag"></i>
                    }
                    <span class="header-tags-filter-popover__selected-chip-label">{{ tag.name }}</span>
                    <span class="header-tags-filter-popover__selected-chip-remove" aria-hidden="true">×</span>
                  </button>
                </div>
                <!-- tasks/frontend-tagging-phase3.md — secondary to picking tags, so only shown once one is picked. -->
                <div class="header-tag-scope">
                  <span class="header-tag-scope__label" id="quickTagScopeLabel">Scope</span>
                  <p-selectbutton
                    [options]="tagScopeOptions"
                    [ngModel]="activeTagScope"
                    [allowEmpty]="false"
                    optionLabel="label"
                    optionValue="value"
                    optionDisabled="disabled"
                    size="small"
                    ariaLabelledBy="quickTagScopeLabel"
                    (ngModelChange)="setQuickTagScope($event)">
                    <ng-template #item let-option>
                      <span class="inline-flex items-center gap-1 text-xs" [title]="option.disabled ? 'Private tags can\'t be filtered yet: the server doesn\'t provide them in bulk' : ''">
                        @if (option.icon) { <i [class]="option.icon + ' !text-[0.625rem]'" aria-hidden="true"></i> }{{ option.label }}
                      </span>
                    </ng-template>
                  </p-selectbutton>
                </div>
              </div>

              <div class="header-tags-filter-popover__list">
                @for (group of quickTagOptionGroups; track group.scope) {
                  <div class="header-tag-group-label">
                    @if (group.scope === TAG_SCOPE.PRIVATE) {
                      <i class="pi pi-lock !text-[0.625rem]" aria-hidden="true"></i>
                    }
                    {{ group.label }}
                  </div>
                  <button
                    *ngFor="let tag of group.items"
                    type="button"
                    class="header-tags-filter-popover__item"
                    [class.header-tags-filter-popover__item--active]="isQuickTagSelected(tag._id)"
                    (click)="toggleQuickTag(tag._id)">
                    <span class="header-tag-option">
                      <input
                        type="checkbox"
                        class="header-tags-filter-popover__checkbox"
                        [checked]="isQuickTagSelected(tag._id)"
                        (click)="$event.stopPropagation()"
                        (change)="toggleQuickTag(tag._id)" />
                      <span class="header-tag-option__swatch" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
                      @if (isPrivateTagOption(tag)) {
                        <i class="pi pi-lock !text-[0.625rem] opacity-70" aria-label="Private tag"></i>
                      }
                      <span class="header-tag-option__label">{{ tag.name }}</span>
                      <span class="header-tag-option__context">{{ tagContextLabel(tag) }}</span>
                    </span>
                  </button>
                }
                <div *ngIf="quickTagOptions.length === 0" class="header-tags-filter-popover__empty">
                  No tags match.
                </div>
              </div>

              <div class="header-tags-filter-popover__actions">
                <p-button
                  label="Clear"
                  severity="secondary"
                  size="small"
                  (onClick)="clearQuickTagFilter()">
                </p-button>
                <p-button
                  label="Advanced"
                  icon="pi pi-sliders-h"
                  size="small"
                  (onClick)="openAdvancedFromQuickTag(quickTagPopover)">
                </p-button>
              </div>
            </div>
          </p-popover>
        </div>
        <div class="flex items-center ml-2 header-date-filter-shell">
          <button
            type="button"
            pButton
            class="header-filter-button header-filter-button--date"
            [ngClass]="{ 'filter-active-border': isDateFilterActive }"
            (click)="dateFilterPopover.toggle($event)"
            [title]="dateFilterTooltipLabel"
            aria-label="Filter by date">
            <span class="material-symbols-outlined header-date-filter-btn__icon" aria-hidden="true">calendar_month</span>
            <span class="header-date-filter-btn__label">{{ dateFilterButtonLabel }}</span>
          </button>

          <p-popover
            #dateFilterPopover
            [dismissable]="true"
            appendTo="body"
            styleClass="header-date-filter-popover">
            <div class="header-date-filter-popover__content">
              <div class="header-date-filter-popover__title">Filter by date</div>

              <p-select
                [options]="dateFieldOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="dateFilterField"
                appendTo="body"
                placeholder="Date field"
                (onChange)="onDateFilterFieldChange($event.value)">
              </p-select>

              <p-select
                [options]="dateModeOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="dateFilterMode"
                appendTo="body"
                placeholder="Since mode"
                (onChange)="onDateFilterModeChange($event.value)">
              </p-select>

              <div *ngIf="dateFilterMode === 'days'" class="header-date-filter-popover__days">
                <p-inputNumber
                  inputId="dateSinceDays"
                  [(ngModel)]="dateSinceDays"
                  [useGrouping]="false"
                  [min]="1"
                  [showButtons]="true"
                  [step]="1"
                  placeholder="Days"
                  (ngModelChange)="onDateSinceDaysChange($event)">
                </p-inputNumber>
                <div class="header-date-filter-popover__quick-days">
                  <p-button
                    *ngFor="let option of sinceDayQuickOptions"
                    size="small"
                    severity="secondary"
                    [outlined]="dateSinceDays !== option.value"
                    [label]="option.label"
                    (onClick)="onDateQuickPick(option.value)">
                  </p-button>
                </div>
              </div>

              <p-datepicker
                *ngIf="dateFilterMode === 'date'"
                inputId="dateSinceSpecific"
                [showIcon]="true"
                dateFormat="dd.mm.yy"
                [(ngModel)]="dateSinceDate"
                (ngModelChange)="onDateSinceDateChange($event)">
              </p-datepicker>

              <div class="header-date-filter-popover__actions">
                <p-button
                  label="Clear"
                  severity="secondary"
                  size="small"
                  (onClick)="clearDateFilters(); dateFilterPopover.hide()">
                </p-button>
              </div>
            </div>
          </p-popover>
        </div>
        <div class="mx-2">
          <p-select
            [options]="statusFilterOptions"
            appendTo="body"
            [panelStyleClass]="'status-select-panel project-status-panel'"
            class="!p-0 mx-2 my-0 text-sm leading-none h-9 w-36 status-select"
            [ngClass]="{ 'filter-active-border': isStatusFilterActive }"
            [(ngModel)]="selectedStatus"
            (onChange)="onStatusChanged($event.value)"
            placeholder="Status"
            aria-label="Status filter">
          </p-select>
        </div>
        <div class="header-saved-search-shell">
          <p-select
            [(ngModel)]="selectedSavedSearchId"
            [options]="savedSearches"
            optionLabel="name"
            optionValue="id"
            [filter]="true"
            filterBy="name"
            [showClear]="true"
            placeholder="Presets"
            appendTo="body"
            panelStyleClass="header-saved-search-panel"
            class="header-saved-search-select h-9 text-sm"
            [ngClass]="{ 'filter-active-border': !!selectedSavedSearchId }"
            (onChange)="onSavedSearchSelected($event.value)"
            (onShow)="focusSavedSearchFilterInput()">
          </p-select>
        </div>
        <div class="flex items-center gap-2 header-quick-actions">
          <p-button
            size="small"
            styleClass="p-0 header-filter-button header-filter-button--action"
            title="New List window"
            aria-label="New List window"
            data-testid="header-new-list-window"
            (onClick)="openNewListWindow()">
            <span class="material-symbols-outlined text-base" aria-hidden="true">view_list</span>
          </p-button>
          <p-button
            size="small"
            styleClass="p-0 header-filter-button header-filter-button--action"
            title="New Tasks window"
            aria-label="New Tasks window"
            data-testid="header-new-tasks-window"
            (onClick)="openNewTasksWindow()">
            <span class="material-symbols-outlined text-base" aria-hidden="true">checklist</span>
          </p-button>
          <p-button
            size="small"
            styleClass="p-0 header-filter-button header-filter-button--action"
            title="New Calendar window"
            aria-label="New Calendar window"
            (onClick)="openNewCalendarWindow()">
            <span class="material-symbols-outlined text-base" aria-hidden="true">calendar_month</span>
          </p-button>
        </div>
        <span
          class="text-xs w-5 flex-grow text-right font-light text-surface-700 dark:text-surface-100 leading-none whitespace-nowrap"
          >{{ auth.getFormattedRemainingTime() }}</span
        >
      </div>
      <!-- End of filter-container -->
    </div>
    <!-- END Large-Screen App-Head pane -->
  </div>
</div>

<!-- ============================================================
     Shared: Advanced Tag Filter content template
     Used by the desktop p-popover and the mobile p-dialog.
     ============================================================ -->
<ng-template #advancedTagFilterContent>
  <div class="advanced-tag-filter-panel">
    <div class="advanced-tag-filter-panel__title">Advanced Tag Filter</div>

    <!-- Include tags -->
    <div class="advanced-tag-filter-panel__group">
      <label class="advanced-tag-filter-panel__label advanced-tag-filter-panel__label--include">
        <i class="pi pi-plus-circle"></i> Include Tags
      </label>
      <p-select
        [options]="includeTagModeOptions"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="pendingIncludeTagMode"
        appendTo="body"
        styleClass="w-full advanced-tag-filter-panel__mode-select">
      </p-select>
      <p-multiselect
        [options]="groupedTagOptions"
        [group]="true"
        optionGroupLabel="label"
        optionGroupChildren="items"
        optionLabel="name"
        optionValue="_id"
        [(ngModel)]="pendingIncludeTagIds"
        [filter]="true"
        [showClear]="true"
        display="chip"
        placeholder="Select tags to include"
        appendTo="body"
        [scrollHeight]="'40vh'"
        styleClass="w-full"
        (onChange)="onAdvancedIncludeChange($event.value)">
        <ng-template let-group pTemplate="group">
          <div class="header-tag-group-label">
            @if (group.scope === TAG_SCOPE.PRIVATE) {
              <i class="pi pi-lock !text-[0.625rem]" aria-hidden="true"></i>
            }
            {{ group.label }}
          </div>
        </ng-template>
        <ng-template let-tag pTemplate="item">
          <div class="header-tag-option">
            <span class="header-tag-option__swatch" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
            @if (isPrivateTagOption(tag)) {
              <i class="pi pi-lock !text-[0.625rem] opacity-70" aria-label="Private tag"></i>
            }
            <span>{{ tag.name }}</span>
            <span class="header-tag-option__context">{{ tagContextLabel(tag) }}</span>
          </div>
        </ng-template>
      </p-multiselect>
      <span class="advanced-tag-filter-panel__hint">
        {{ pendingIncludeTagMode === 'OR' ? 'Note must contain ANY selected include tag (OR logic)' : 'Note must contain ALL selected include tags (AND logic)' }}
      </span>
    </div>

    <!-- Exclude tags -->
    <div class="advanced-tag-filter-panel__group">
      <label class="advanced-tag-filter-panel__label advanced-tag-filter-panel__label--exclude">
        <i class="pi pi-minus-circle"></i> Exclude Tags
      </label>
      <p-multiselect
        [options]="groupedTagOptions"
        [group]="true"
        optionGroupLabel="label"
        optionGroupChildren="items"
        optionLabel="name"
        optionValue="_id"
        [(ngModel)]="pendingExcludeTagIds"
        [filter]="true"
        [showClear]="true"
        display="chip"
        placeholder="Select tags to exclude"
        appendTo="body"
        [scrollHeight]="'40vh'"
        styleClass="w-full"
        (onChange)="onAdvancedExcludeChange($event.value)">
        <ng-template let-group pTemplate="group">
          <div class="header-tag-group-label">
            @if (group.scope === TAG_SCOPE.PRIVATE) {
              <i class="pi pi-lock !text-[0.625rem]" aria-hidden="true"></i>
            }
            {{ group.label }}
          </div>
        </ng-template>
        <ng-template let-tag pTemplate="item">
          <div class="header-tag-option">
            <span class="header-tag-option__swatch" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
            @if (isPrivateTagOption(tag)) {
              <i class="pi pi-lock !text-[0.625rem] opacity-70" aria-label="Private tag"></i>
            }
            <span>{{ tag.name }}</span>
            <span class="header-tag-option__context">{{ tagContextLabel(tag) }}</span>
          </div>
        </ng-template>
      </p-multiselect>
      <span class="advanced-tag-filter-panel__hint">Notes with ANY of these tags are hidden</span>
    </div>

    <!-- Tag scope (tasks/frontend-tagging-phase3.md): applies to the include and exclude tags above. -->
    <div class="advanced-tag-filter-panel__group">
      <div class="header-tag-scope">
                  <span class="header-tag-scope__label" id="advancedTagScopeLabel">Scope</span>
                  <p-selectbutton
                    [options]="tagScopeOptions"
                    [(ngModel)]="pendingTagScope"
                    [allowEmpty]="false"
                    optionLabel="label"
                    optionValue="value"
                    optionDisabled="disabled"
                    size="small"
                    ariaLabelledBy="advancedTagScopeLabel">
                    <ng-template #item let-option>
                      <span class="inline-flex items-center gap-1 text-xs" [title]="option.disabled ? 'Private tags can\'t be filtered yet: the server doesn\'t provide them in bulk' : ''">
                        @if (option.icon) { <i [class]="option.icon + ' !text-[0.625rem]'" aria-hidden="true"></i> }{{ option.label }}
                      </span>
                    </ng-template>
                  </p-selectbutton>
                </div>
      <span class="advanced-tag-filter-panel__hint">
        @switch (pendingTagScope) {
          @case (TAG_FILTER_SCOPE.SPACE) { Match Space tags only, by name, in every Space }
          @case (TAG_FILTER_SCOPE.PRIVATE) { Match your private tags only }
          @default { Match Space and private tags with the selected names }
        }
        @if (!privateTagFilterAvailable) {
          <br />Private tags can't be filtered yet — the server doesn't provide them in bulk.
        }
      </span>
    </div>

    <!-- Actions -->
    <div class="advanced-tag-filter-panel__actions">
      <p-button
        label="Clear All"
        severity="secondary"
        size="small"
        icon="pi pi-filter-slash"
        (onClick)="clearAdvancedTagFilter()">
      </p-button>
      <p-button
        label="Cancel"
        severity="secondary"
        size="small"
        icon="pi pi-times"
        (onClick)="cancelAdvancedTagFilter()">
      </p-button>
      <p-button
        label="Apply"
        severity="primary"
        size="small"
        icon="pi pi-check"
        (onClick)="applyAdvancedTagFilter()">
      </p-button>
    </div>
  </div>
</ng-template>

<!-- Mobile Space filter dialog (tasks/space_ux.md), opened from the mobile Filter menu's
     "🌐 Spaces..." entry. Pinned to the top and an always-open checkbox list with no text input, so
     every Space is one tap away and the iOS keyboard never pops up over it. Same selection
     semantics as the desktop inline multiselect (onSpaceFilterChanged). -->
<p-dialog
  header="Filter by Space"
  [(visible)]="spaceFilterDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  position="top"
  [style]="{ width: 'min(96vw, 420px)', marginTop: 'max(0.75rem, env(safe-area-inset-top))' }"
  appendTo="body">
  <p-listbox
    [options]="spaceFilterOptions"
    optionLabel="label"
    optionValue="id"
    [multiple]="true"
    [checkbox]="true"
    [showToggleAll]="false"
    [metaKeySelection]="false"
    [filter]="false"
    [ngModel]="selectedSpaceIds"
    (ngModelChange)="onSpaceFilterChanged($event)"
    scrollHeight="60vh"
    styleClass="w-full space-filter-listbox"
    ariaLabel="Space filter">
    <ng-template #item let-option>
      <ng-container *ngTemplateOutlet="spaceFilterLabel; context: { $implicit: option }"></ng-container>
    </ng-template>
  </p-listbox>
  <div class="mt-3 flex justify-end">
    <p-button label="Done" (onClick)="spaceFilterDialogVisible = false"></p-button>
  </div>
</p-dialog>

<!-- A Space filter option's label: shared Spaces red with the "group" icon (desktop multiselect and mobile dialog). -->
<ng-template #spaceFilterLabel let-option>
  @if (option?.shared) {
    <span class="space-filter-option--shared inline-flex items-center gap-0.5 text-red-600 dark:text-red-400" title="Shared Space">
      <span class="material-symbols-outlined !text-sm leading-none" aria-hidden="true">group</span>{{ option.label }}
    </span>
  } @else {
    <span>{{ option?.label }}</span>
  }
</ng-template>

<!-- Advanced tag filter dialog — used on mobile; also triggered from mobile Advanced button -->
<p-dialog
  header="Advanced Tag Filter"
  [(visible)]="advancedTagDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(96vw, 480px)' }"
  [contentStyle]="{ padding: '0' }"
  appendTo="body"
  styleClass="advanced-tag-filter-dialog"
  (onHide)="cancelAdvancedTagFilter()">
  <ng-container *ngTemplateOutlet="advancedTagFilterContent"></ng-container>
</p-dialog>
